import { useAuth as useClerkAuth } from '@clerk/clerk-react'
import { LogOut, RefreshCw } from 'lucide-react'
import { performBackofficeLogout } from '../lib/entryContext'

interface AccountLoadErrorProps {
  /** Loads the account again. */
  onRetry: () => void
  /** True while that retry is running. */
  retrying?: boolean
  /** The technical reason, shown small so it can be passed on to support. */
  detail?: string | null
}

/**
 * Shown when the account (GET /api/me) or the company list could not be loaded.
 * A failed load is not the same as having no company, so this never offers
 * "Apply to join": it offers Retry and Log out.
 */
export default function AccountLoadError({ onRetry, retrying = false, detail }: AccountLoadErrorProps) {
  const { signOut } = useClerkAuth()

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-6 py-12 text-foreground">
      <div className="max-w-md text-center space-y-6">
        <div role="alert" className="space-y-2">
          <h1 className="font-display text-2xl font-semibold tracking-tight">Couldn&apos;t load your account</h1>
          <p className="text-sm text-foreground-muted leading-relaxed">
            We couldn&apos;t load your companies and roles, so we can&apos;t tell which pages to show you. This is
            usually a network or server hiccup. Try again in a moment.
          </p>
        </div>
        {detail && <p className="text-xs text-foreground-subtle break-words">Details: {detail}</p>}
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-white hover:bg-accent-strong transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <RefreshCw size={16} className={retrying ? 'animate-spin' : undefined} aria-hidden="true" />
            {retrying ? 'Retrying…' : 'Retry'}
          </button>
          <button
            type="button"
            onClick={() => performBackofficeLogout(signOut)}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-border px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-surface-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <LogOut size={16} aria-hidden="true" />
            Log out
          </button>
        </div>
        <p role="status" className="sr-only">
          {retrying ? 'Loading your account again…' : ''}
        </p>
      </div>
    </div>
  )
}
